/**
 * Inclusão de ligações em lote.
 *
 * **O que o legado faz em cinco telas.** `IncluirLigacoes`, `IncluirLigacoesTudo`
 * e `IncluirLigacoesAterramento` mostram a mesma lista com dois eixos
 * diferentes; os dois `Finalizar` gravam o que foi marcado. Aqui são dois
 * interruptores e uma lista.
 *
 * **Marca-se a linha, e só ela entra.** A linha é o pinout do candidato mais o
 * pinout de onde ele sai, e é esse par que vai para o servidor. Antes ia o
 * cruzamento de duas listas (pinouts × componentes), como no legado, e o mesmo
 * pinout em mais de uma linha marcava todas: é o caso do aterramento — o ponto de
 * massa é um pinout só para todo pino que aterra nele, e incluir um trazia os do
 * circuito inteiro, juntos num bloco. Marcar o componente marca as linhas dele.
 *
 * **Isto grava.** Não é a RN-15 — cria componente de diagrama e terminal de
 * verdade. Por isso o botão diz quantos pares vão, e a resposta diz quantos
 * entraram: os dois números raramente são iguais, porque o servidor pula o par
 * que já existe.
 */

import { useMemo, useState } from "react"
import {
  chaveDoPar, usarCandidatosDeLigacao, usarIncluirLigacoes,
  type ComponenteCandidato, type LinhaMarcada, type PinoCandidato, type ResultadoDaInclusao,
} from "@/api/ligacoesEmLote"
import { ErroDaApi, ErroDeTrava } from "@/api/erros"
import { emOrdemDePinoPrincipal, NomeDoCandidato, PinosDoCandidato } from "./CandidatoDeLigacao"

function mensagem(e: unknown): string {
  if (e instanceof ErroDeTrava) return e.message
  if (e instanceof ErroDaApi) return e.message
  return e instanceof Error ? e.message : "Não foi possível gravar."
}

/** O nome do lado, como o desenho o chama. */
function lado(posicao: string): string {
  return posicao === "left" ? "esquerda" : posicao === "right" ? "direita" : posicao
}

export function LigacoesEmLote({ projetoId, diagramaId, aoIncluir }: {
  projetoId: number
  diagramaId: number
  /** Avisa quem mostra o desenho, para ele se recarregar com as ligações novas. */
  aoIncluir?: (r: ResultadoDaInclusao) => void
}) {
  const [completo, setCompleto] = useState(false)
  const [aterramento, setAterramento] = useState(true)

  const { data, isPending, error } = usarCandidatosDeLigacao(diagramaId, completo, aterramento)
  const incluir = usarIncluirLigacoes(projetoId, diagramaId)

  // As linhas marcadas, pela chave do par.
  const [marcadas, setMarcadas] = useState<Map<string, LinhaMarcada>>(new Map())
  const [resultado, setResultado] = useState<ResultadoDaInclusao | null>(null)

  // Na ordem do legado, pelo pino principal.
  const itens = useMemo(() => emOrdemDePinoPrincipal(data ?? []), [data])

  const pares = marcadas.size
  const linha = (c: ComponenteCandidato, l: PinoCandidato): LinhaMarcada =>
    ({ pinoutId: l.pinoutId, ligacaoPinoutId: l.ligacaoPinoutId, componenteId: c.componenteId })

  function alternarPino(c: ComponenteCandidato, l: PinoCandidato) {
    setResultado(null)
    setMarcadas((m) => {
      const n = new Map(m)
      const chave = chaveDoPar(l)
      n.has(chave) ? n.delete(chave) : n.set(chave, linha(c, l))
      return n
    })
  }

  function alternarComponente(c: ComponenteCandidato) {
    setResultado(null)
    const ligado = c.ligacoes.some((l) => marcadas.has(chaveDoPar(l)))
    setMarcadas((m) => {
      const n = new Map(m)
      for (const l of c.ligacoes) ligado ? n.delete(chaveDoPar(l)) : n.set(chaveDoPar(l), linha(c, l))
      return n
    })
  }

  function marcarTudo(ligar: boolean) {
    setResultado(null)
    setMarcadas(ligar
      ? new Map(itens.flatMap((c) => c.ligacoes.map((l) => [chaveDoPar(l), linha(c, l)] as const)))
      : new Map())
  }

  function gravar() {
    incluir.mutate(
      { pares: [...marcadas.values()], completo, aterramento },
      {
        onSuccess: (r) => {
          setResultado(r)
          setMarcadas(new Map())
          aoIncluir?.(r)
        },
      },
    )
  }

  function trocarEixo(mudar: () => void) {
    // A lista muda inteira: uma selecao montada sobre a lista anterior nao vale
    // mais, e manter os ids seria gravar o que a tela nao esta mostrando.
    setMarcadas(new Map())
    setResultado(null)
    mudar()
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <header style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Incluir ligações</h3>

        <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--txt2)" }}>
          <input
            type="checkbox" checked={completo}
            onChange={(e) => trocarEixo(() => setCompleto(e.target.checked))}
          />
          Tudo (inclui circuito de aterramento)
        </label>

        <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--txt2)" }}>
          <input
            type="checkbox" checked={!aterramento}
            onChange={(e) => trocarEixo(() => setAterramento(!e.target.checked))}
          />
          Não simplificar o aterramento
        </label>

        <span style={{ flex: 1 }} />

        <button onClick={() => marcarTudo(true)} style={{ fontSize: 11 }} disabled={itens.length === 0}>
          Marcar todos
        </button>
        <button onClick={() => marcarTudo(false)} style={{ fontSize: 11 }} disabled={pares === 0}>
          Limpar
        </button>
      </header>

      {isPending && (
        <p style={{ fontSize: 11, color: "var(--txt2)", margin: 0 }}>
          Montando o diagrama para descobrir os candidatos…
        </p>
      )}

      {error && (
        <p style={{ fontSize: 11, color: "var(--erro)", margin: 0 }}>{mensagem(error)}</p>
      )}

      {!isPending && !error && itens.length === 0 && (
        <p style={{ fontSize: 11, color: "var(--txt2)", margin: 0 }}>
          Nenhum candidato — tudo o que o motor monta já está no diagrama.
        </p>
      )}

      {itens.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {itens.map((c) => {
            const ligado = c.ligacoes.some((l) => marcadas.has(chaveDoPar(l)))
            return (
              <div
                key={`${c.componenteId}-${c.posicao}-${c.ordem}`}
                style={{
                  border: "1px solid var(--line)", borderRadius: 6, padding: "8px 10px",
                  background: ligado ? "var(--surface2)" : "var(--surface)",
                }}
              >
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  <input type="checkbox" checked={ligado} onChange={() => alternarComponente(c)} />
                  <NomeDoCandidato candidato={c} />
                  <span style={{ color: "var(--txt2)", fontSize: 11 }}>
                    {lado(c.posicao)} · ordem {c.ordem} · {c.ligacoes.length} pino
                    {c.ligacoes.length === 1 ? "" : "s"}
                  </span>
                </label>

                <div style={{ marginTop: 6, paddingLeft: 20 }}>
                  <PinosDoCandidato
                    ligacoes={c.ligacoes}
                    marcado={(l) => marcadas.has(chaveDoPar(l))}
                    aoAlternar={(l) => alternarPino(c, l)}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}

      <footer style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={gravar} disabled={pares === 0 || incluir.isPending} style={{ fontSize: 11 }}>
          {incluir.isPending ? "Gravando…" : `Incluir ${pares} ${pares === 1 ? "ligação" : "ligações"}`}
        </button>

        {incluir.error && (
          <span style={{ fontSize: 11, color: "var(--erro)" }}>{mensagem(incluir.error)}</span>
        )}

        {resultado && (
          <span style={{ fontSize: 11, color: "var(--txt2)" }}>
            {resultado.ligacoesInseridas} {resultado.ligacoesInseridas === 1 ? "ligação" : "ligações"}
            {" e "}{resultado.componentesInseridos} componente
            {resultado.componentesInseridos === 1 ? "" : "s"} entraram — o desenho gravado ficou para trás.
          </span>
        )}
      </footer>
    </section>
  )
}
